import { z } from 'zod';
import { PlanInput, PlanReviewOutcome } from './run-settings.js';

export const RUN_MODES = ['chat', 'task', 'subagent'] as const;
export type RunMode = (typeof RUN_MODES)[number];

export const RUN_STATUSES = [
  'queued',
  'running',
  /** Paused on a subscription usage limit; resumes by itself at `resumeAt` (or on "continue"). */
  'scheduled',
  'awaiting_approval',
  'awaiting_human',
  'waiting_children',
  'completed',
  'failed',
  'cancelled',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
export const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set(['completed', 'failed', 'cancelled']);

export const TASK_STATUSES = [
  'todo',
  'queued',
  'running',
  'awaiting_approval',
  'waiting_children',
  'done',
  'failed',
  'cancelled',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** The subscription CLIs a run can hand off between when one hits its usage limit. */
export const FALLBACK_RUNTIMES = ['claude-agent-sdk', 'codex-sdk'] as const;
export type FallbackRuntime = (typeof FALLBACK_RUNTIMES)[number];
export const FALLBACK_REASONS = ['usage_limit', 'cooling_down', 'subscription_recovered'] as const;
export type FallbackReason = (typeof FALLBACK_REASONS)[number];

/**
 * Events appended to `run_events` and streamed to clients over SSE (`id:` = seq).
 * Text deltas are coalesced by the worker (~100ms) before being written.
 */
export const RunEvent = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('plan-submitted'),
    approvalId: z.string(),
    version: z.number().int(),
    plan: PlanInput,
  }),
  z.object({
    type: z.literal('plan-reviewed'),
    approvalId: z.string(),
    reviewerAgentId: z.string(),
    review: PlanReviewOutcome,
  }),
  z.object({
    type: z.literal('plan-decided'),
    approvalId: z.string(),
    decision: z.enum(['approve', 'request_changes', 'reject']),
    reviewerId: z.string(),
    reason: z.string().nullable(),
  }),
  z.object({ type: z.literal('plan-execution'), approvalId: z.string(), version: z.number().int() }),
  z.object({
    type: z.literal('status'),
    status: z.enum(RUN_STATUSES),
    error: z.string().optional(),
    /** `scheduled` only: why the run waits, on which provider, and when it resumes (ISO). */
    reason: z.string().optional(),
    provider: z.string().optional(),
    resumeAt: z.string().optional(),
  }),
  z.object({ type: z.literal('text-delta'), stepIdx: z.number().int(), delta: z.string() }),
  z.object({ type: z.literal('reasoning-delta'), stepIdx: z.number().int(), delta: z.string() }),
  z.object({
    type: z.literal('tool-call'),
    stepIdx: z.number().int(),
    toolCallId: z.string(),
    toolName: z.string(),
    input: z.unknown(),
    effect: z.string(),
  }),
  z.object({
    type: z.literal('tool-result'),
    toolCallId: z.string(),
    toolName: z.string(),
    output: z.unknown(),
    isError: z.boolean().default(false),
  }),
  z.object({
    type: z.literal('approval-requested'),
    approvalId: z.string(),
    toolCallId: z.string(),
    toolName: z.string(),
    inputPreview: z.string(),
  }),
  z.object({
    type: z.literal('approval-decided'),
    approvalId: z.string(),
    approved: z.boolean(),
    decidedBy: z.string().optional(),
  }),
  z.object({
    type: z.literal('step-finished'),
    stepIdx: z.number().int(),
    usage: z.object({
      inputTokens: z.number(),
      outputTokens: z.number(),
      cacheReadTokens: z.number(),
      cacheWriteTokens: z.number(),
      reasoningTokens: z.number(),
    }),
    costUsd: z.number(),
  }),
  z.object({ type: z.literal('subagent-spawned'), childRunId: z.string(), role: z.string() }),
  /** Messages from the run inbox (other agents, later chat users) handed to the model mid-run; redacted + short. */
  z.object({
    type: z.literal('message-delivered'),
    stepIdx: z.number().int(),
    messages: z.array(z.object({ id: z.string(), source: z.string(), from: z.string(), text: z.string() })),
  }),
  z.object({
    type: z.literal('log'),
    level: z.enum(['debug', 'info', 'warn', 'error']),
    message: z.string(),
  }),
  /**
   * The egress proxy refused hosts that are not on the sandbox's allowlist (seen after a failed command).
   * `agentId` owns the sandbox; the run view offers to allow them.
   */
  z.object({
    type: z.literal('egress-blocked'),
    agentId: z.string(),
    hosts: z.array(z.string()),
    /** The Approvals-inbox requests behind the hosts (decide them there or with "allow" in the run view). */
    requests: z.array(z.object({ id: z.string(), host: z.string() })).optional(),
  }),
  /**
   * The run continues on the other subscription runtime (Claude ⇄ Codex) because the first one hit its usage
   * limit during the turn (`usage_limit`) or was already cooling down before it (`cooling_down`). Model refs
   * are `provider-slug:model` only: never a credential.
   */
  z.object({
    type: z.literal('model-fallback'),
    from: z.enum(FALLBACK_RUNTIMES),
    to: z.enum(FALLBACK_RUNTIMES),
    fromModelRef: z.string().optional(),
    toModelRef: z.string(),
    /** Effort the new model actually runs with; absent when none applies or it is the agent's own (return to preferred). */
    toReasoningEffort: z.enum(['none', 'low', 'medium', 'high']).optional(),
    reason: z.enum(FALLBACK_REASONS),
  }),
  z.object({ type: z.literal('finish'), finishReason: z.string(), totalCostUsd: z.number() }),
  /** Flow runs: progress of one flow step (tool calls / usage of the step are emitted as usual). */
  z.object({
    type: z.literal('flow-step'),
    stepId: z.string(),
    kind: z.string(),
    title: z.string(),
    status: z.enum(['started', 'ok', 'skipped', 'failed', 'waiting']),
    costUsd: z.number().optional(),
    error: z.string().optional(),
  }),
]);
export type RunEvent = z.infer<typeof RunEvent>;

/** Domain events written to `events_outbox`; consumed by trigger matcher + notification router. */
export const DOMAIN_EVENT_TYPES = [
  'run.completed',
  'run.failed',
  'task.done',
  'task.failed',
  'approval.requested',
  'approval.decided',
  'budget.warn',
  'budget.exceeded',
  'webhook.received',
  'agent.message',
  'org.halted',
  'human.question',
  /** The egress proxy refused hosts of an agent's sandbox: waiting for a person in the Approvals inbox. */
  'egress.requested',
  'egress.decided',
  'trigger.circuit_open',
  /** An MCP server stopped accepting its credential (OAuth refresh failed / 401 / 403). */
  'mcp.auth_failed',
  /** A billing account crossed 80% / 100% of a plan limit (once per threshold per quota period). */
  'plan.limit',
  /** Anthropic stopped accepting a person's own Claude Code login (docs/claude-login-sandbox.md). */
  'provider.login_expired',
  /** Hosted: a stored subscription token / ChatGPT login was removed from a provider (subscription-purge.ts). */
  'provider.subscription_removed',
] as const;
export type DomainEventType = (typeof DOMAIN_EVENT_TYPES)[number];

export const NOTIFICATION_CHANNEL_TYPES = [
  'inapp',
  'webpush',
  'telegram',
  'email',
  'slack',
  'discord',
  'apprise',
] as const;
export type NotificationChannelType = (typeof NOTIFICATION_CHANNEL_TYPES)[number];

/** Postgres LISTEN/NOTIFY channels. Payloads are ids only (NOTIFY payload limit 8KB). */
export const PG_CHANNELS = {
  /** payload: `<runId>:<seq>` — a new run_events row exists. */
  runEvents: 'af_run_events',
  /** payload: `<orgId>:<kind>` — org-level change (agent status, approval, notification, halt). */
  orgEvents: 'af_org_events',
  /** payload: `<outboxId>` — new events_outbox row. */
  outbox: 'af_outbox',
} as const;
